import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Link, MenuItem, Pagination, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { PaymentRunService } from '../../../services/paymentRun.service';
import { PaymentRunStatus, PaymentRunSummary } from '../../../interfaces/procurement/paymentRun.interface';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import ScrollableTable from '../../common/ScrollableTable';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';

const STATUSES: PaymentRunStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'EXECUTED', 'CANCELLED', 'REVERSED'];

export const statusColor = (s: PaymentRunStatus): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (s) {
    case 'EXECUTED': return 'success';
    case 'APPROVED': return 'primary';
    case 'SUBMITTED': return 'info';
    case 'REJECTED': return 'error';
    case 'REVERSED': return 'warning';
    default: return 'default';
  }
};

export const PaymentRunList: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const canManage = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);
  const [rows, setRows] = useState<PaymentRunSummary[]>([]);
  const [status, setStatus] = useState<PaymentRunStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const r = await PaymentRunService.list(AuthService.getSelectedInstitution()!!, status || undefined, page, 20);
      setRows(r.content);
      setPageInfo({ currentPage: r.number, totalPages: r.totalPages, totalElements: r.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load payment runs');
    } finally {
      setLoading(false);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(0); }, [load]);

  return (
    <Box data-help-page="paymentRun.list.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Payment Runs</Typography>
        {canManage && <Button data-help="paymentRun.list.new" variant="contained" onClick={() => navigate('/procurement/payment-runs/new')}>New payment run</Button>}
      </Box>
      <Box data-help-section="paymentRun.list.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="paymentRun.list.status" select size="small" label="Status" value={status} sx={{ flex: '1 1 200px', maxWidth: 260 }}
          onChange={e => setStatus(e.target.value as PaymentRunStatus | '')}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
      </Box>
      {loading ? <Typography>Loading...</Typography> : rows.length === 0 ? (
        <Typography color="text.secondary">No payment runs.</Typography>
      ) : (
        <Paper>
          <ScrollableTable>
            <Table data-help-section="paymentRun.list.table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="paymentRun.list.col.run">Run</TableCell><TableCell data-help="paymentRun.list.col.status">Status</TableCell><TableCell data-help="paymentRun.list.col.method">Method</TableCell>
                  <TableCell data-help="paymentRun.list.col.date">Payment date</TableCell><TableCell data-help="paymentRun.list.col.bank">Pay from</TableCell>
                  <TableCell data-help="paymentRun.list.col.bills" align="right">Bills</TableCell><TableCell data-help="paymentRun.list.col.settled" align="right">Settled</TableCell>
                  <TableCell data-help="paymentRun.list.col.cash" align="right">Cash out</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><Link data-help="paymentRun.list.action.open" component={RouterLink} to={`/procurement/payment-runs/${r.id}`}>{r.runNo}</Link></TableCell>
                    <TableCell><Chip size="small" color={statusColor(r.status)} label={humanizeEnum(r.status)} /></TableCell>
                    <TableCell>{r.method}</TableCell>
                    <TableCell>{formatDate(r.paymentDate)}</TableCell>
                    <TableCell>{r.bankAccountLabel ?? '—'}</TableCell>
                    <TableCell align="right">{r.lineCount}</TableCell>
                    <TableCell align="right">{formatMoney(r.totalSettle)}</TableCell>
                    <TableCell align="right">{formatMoney(r.totalNet)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        </Paper>
      )}
      {pageInfo.totalPages > 1 && (
        <Pagination sx={{ mt: 2 }} count={pageInfo.totalPages} page={pageInfo.currentPage + 1}
          onChange={(_, p) => load(p - 1)} />
      )}
    </Box>
  );
};

export default PaymentRunList;
